import { useCallback, useMemo, useRef } from "react";
import styled from "styled-components";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Token } from "@sablier/v2-models";
import { IToken } from "@sablier/v2-types";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList as List } from "react-window";
import { useModalTokenPicker, useTokensLocal } from "~/client/hooks";
import { Empty, Item } from "../common";
import { ItemAction } from "../common/Item";

const WrapperPartial = styled.div`
  flex: 1;
  flex-shrink: 0;
  width: 100%;
`;

const Content = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    flex-shrink: 0;
    width: 100%;
    height: 100%;
    padding-inline: ${(props) => props.theme.sizes.modalEdge};
    padding-top: ${(props) => props.theme.sizes.edge};
    padding-bottom: 0;
  }
`;

const Results = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    flex: 1;
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    width: 100%;
  }
`;

const ListWrapper = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    width: 100%;
    height: 100%;
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    ${Content} {
      row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
      padding: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    }
  }
  ${(props) => props.theme.medias.maxSM} {
    ${Content} {
      row-gap: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
      padding: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
    }
  }
`;

interface ItemData {
  action: ItemAction;
  list: IToken[];
  onClick: (item: IToken) => void;
}

interface Props {
  onHome: () => void;
  onRegistry: () => void;
}

function TokenPickerManage({ onRegistry }: Props) {
  const { t } = useT();
  const virtualListRef = useRef<HTMLDivElement>();
  const { list, removeLocal } = useTokensLocal(false);
  const { data: modalData } = useModalTokenPicker();
  const count = useMemo(() => list.length, [list]);

  const onItemClick = useCallback(
    (item: IToken) => {
      const { chosen } = modalData;
      if (chosen && Token.areEqual(item, chosen)) {
        modalData.onChoose(undefined);
      }
      removeLocal(item);
    },
    [modalData, removeLocal],
  );

  const itemData: ItemData = useMemo(
    () => ({
      action: ItemAction.REMOVE,
      list,
      onClick: onItemClick,
    }),
    [list, onItemClick],
  );

  return (
    <Wrapper>
      <Content>
        <Results>
          <ListWrapper>
            {count === 0 && (
              <Empty
                content={t("structs.noSavedToken")}
                isSaveSuggested
                onSuggest={onRegistry}
              />
            )}
            <AutoSizer>
              {({ height, width }: { width: number; height: number }) => (
                <List<ItemData>
                  height={height ?? 0}
                  itemCount={count}
                  itemData={itemData}
                  itemSize={60}
                  outerRef={virtualListRef}
                  width={width ?? 0}
                >
                  {Item}
                </List>
              )}
            </AutoSizer>
          </ListWrapper>
        </Results>
      </Content>
    </Wrapper>
  );
}

export default TokenPickerManage;
